import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { DINACHARYA_LINK_KEYS } from "@/lib/dinacharyaLinks";

const ROWS = [
  ...DINACHARYA_LINK_KEYS,
  ["score_source", "Score data source (spreadsheet the portal reads)"],
];

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const bad = status.error || status.stale;
  return (
    <span data-testid="dc-links-status"
          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${
            bad ? "bg-warning/20 text-[hsl(28_82%_32%)]"
                : status.source_configured ? "bg-success/15 text-success"
                : "bg-muted text-muted-foreground"}`}>
      {status.error ? "Sheet not responding"
        : status.stale ? `Unchanged ${status.unchanged_days} days`
        : status.source_configured ? `Live · team ${status.team_score ?? "—"}`
        : "No score source"}
    </span>
  );
};

export default function AdminDinacharyaLinks() {
  const { reload } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState("");
  const [status, setStatus] = useState({});

  useEffect(() => {
    if (!tenants.length) return;
    Promise.all(tenants.map((t) =>
      api.get(`/tenants/${t.id}/dinacharya/scores`)
        .then(({ data }) => [t.id, data]).catch(() => [t.id, null])
    )).then((rows) => setStatus(Object.fromEntries(rows)));
  }, [tenants]);

  const load = () =>
    api.get("/tenants").then(({ data }) => {
      setTenants(data);
      setDraft(Object.fromEntries(data.map((t) => [t.id, {
        ...(t.dinacharya_links || {}), score_source: t.dinacharya_score_source || "",
      }])));
    }).catch((e) => toast.error(errMsg(e)));

  useEffect(() => { load(); }, []);

  const set = (tid, key, value) =>
    setDraft((d) => ({ ...d, [tid]: { ...d[tid], [key]: value } }));

  const save = async (t) => {
    const { score_source, ...links } = draft[t.id] || {};
    setBusy(t.id);
    try {
      await api.put(`/tenants/${t.id}`, {
        dinacharya_links: links, dinacharya_score_source: score_source || "",
      });
      toast.success(`${t.name}: Dinacharya links saved`);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const applyDefaults = async (t) => {
    try {
      await api.post(`/tenants/${t.id}/dinacharya/links/apply-defaults`);
      toast.success(`${t.name}: Aikyam's saved links and score sheet applied`);
      load();
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyFrom = (t, sourceId) => {    const src = tenants.find((x) => x.id === sourceId);
    if (!src) return;
    setDraft((d) => ({ ...d, [t.id]: {
      ...(src.dinacharya_links || {}), score_source: src.dinacharya_score_source || "",
    } }));
    toast.info(`Copied ${src.name}'s links — review and press Save`);
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-dc-links-header"
        eyebrow="Administration · Dinacharya"
        title="Dinacharya links"
        description="Attach each client's Daily review tracker, Monthly planner, Monthly review tracker and Score categorisation destinations. The score data source is the shared spreadsheet the portal reads NLT / SLT / ELT scores from — share it as “Anyone with the link can view” and paste the tab URL."
      />

      <div className="space-y-6">
        {tenants.map((t) => (
          <div key={t.id} className="panel p-6" data-testid={`dc-links-card-${t.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-xl text-navy">{t.name}</h2>
                <p className="label-caps mt-1">{t.legal_name || t.industry || "—"}</p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={status[t.id]} />
                <Button data-testid={`dc-links-defaults-${t.id}`} variant="outline"
                        onClick={() => applyDefaults(t)}
                        className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  Apply Aikyam defaults
                </Button>
                <select data-testid={`dc-links-copy-${t.id}`} defaultValue=""
                        onChange={(e) => { copyFrom(t, e.target.value); e.target.value = ""; }}
                        className="h-9 rounded-none border border-border bg-card px-3 text-xs text-navy outline-none">
                  <option value="">Copy links from…</option>
                  {tenants.filter((x) => x.id !== t.id).map((x) => (
                    <option key={x.id} value={x.id}>{x.name}</option>
                  ))}
                </select>
                <Button data-testid={`dc-links-save-${t.id}`} disabled={busy === t.id}
                        onClick={() => save(t)}
                        className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  Save links
                </Button>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {ROWS.map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label className="label-caps">{label}</Label>
                  <Input
                    data-testid={`dc-links-${key}-${t.id}`} className="rounded-none font-mono text-xs"
                    placeholder="https://…"
                    value={draft[t.id]?.[key] || ""}
                    onChange={(e) => set(t.id, key, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
